import { capitalize, toRaw } from 'vue'
import { type DeletableEntity, type Entity, type EntityDict } from '../model'
import { toCamelCase } from '../text'
import { post } from './api'
import { type Cache } from './cache'

interface DocumentSet<T extends Entity> {
  etag?: string
  documents: EntityDict<T>
  isAll: boolean
}

type DocumentsByOrganizationResponse<T extends Record<string, Entity>> = {
  [K in keyof T]?: DocumentSet<T[K]>
}

type OrganizationDocumentsResponse = DocumentsByOrganizationResponse<EntityDict<Entity>>

type OrganizationDocumentsOptions<TType extends string, TStateKey extends string> = {
  state: Record<TStateKey, EntityDict<Entity>>
  typeToStateKey: Record<TType, TStateKey>
  organizationId: () => string | undefined
  cache: Cache
  beforeMerge?: (response: OrganizationDocumentsResponse) => Promise<void>
  removeAfterMerge?: (updatedTypes: TType[]) => string[]
  afterUpdate?: () => Promise<unknown>
}

const currentOrganizationIdKey = 'currentOrganizationId'
const releaseDerivedTypes = ['course', 'prebrief', 'playerTechnique', 'techniqueRationales', 'userCoursePerformance']

export function createOrganizationDocuments<TType extends string, TStateKey extends string>({
  state,
  typeToStateKey,
  organizationId,
  cache,
  beforeMerge,
  removeAfterMerge,
  afterUpdate
}: OrganizationDocumentsOptions<TType, TStateKey>) {
  const types = Object.keys(typeToStateKey) as TType[]
  const etags: { [type: string]: string | undefined } = {}
  let initialUpdateResolve: () => void
  const initialUpdate = new Promise<void>(resolve => (initialUpdateResolve = resolve))

  function getCachedOrganizationId() {
    return cache.get<string>(currentOrganizationIdKey)
  }

  async function switchCachedOrganization(toOrganizationId: string) {
    const cachedOrganizationId = await getCachedOrganizationId()
    const switched = !!cachedOrganizationId && cachedOrganizationId !== toOrganizationId
    if (switched) {
      await cache.clear()
    }
    await cache.set<string>(currentOrganizationIdKey, toOrganizationId)
    return switched
  }

  async function setStateFromCache() {
    await Promise.all(types.map(async x => (state[typeToStateKey[x]] = await cache.getByType<Entity>(x))))
  }

  function whenAvailable(type: TType) {
    return Object.keys(state[typeToStateKey[type]]).length > 0 ? Promise.resolve() : initialUpdate
  }

  function updateEntities(loadAll = false) {
    return updateByTypes(types, loadAll)
  }

  async function updateByTypes(typesToUpdate: TType[], loadAll = false): Promise<void> {
    if (typesToUpdate.length === 0) {
      return
    }
    try {
      await fetchAndMerge(typesToUpdate, loadAll)
    } finally {
      initialUpdateResolve()
    }
  }

  async function fetchAndMerge(typesToUpdate: TType[], loadAll: boolean) {
    const { status, body: response } = await post<OrganizationDocumentsResponse>(
      `organization/${organizationId()}/documents`,
      typesToUpdate.map(x => ({ type: capitalize(x), etag: loadAll ? undefined : etags[x] }))
    )
    if (status !== 200 || !response) {
      console.log(`Failed to update via API using cache for type: ${typesToUpdate.join(', ')}`)
      return
    }

    await beforeMerge?.(response)

    const merges = typesToUpdate.map(x => mergeDocuments(x, response[x], loadAll))
    const removedIds = removeAfterMerge?.(typesToUpdate) ?? []
    typesToUpdate.forEach(type => {
      const etag = response[type]?.etag
      if (loadAll || etag) {
        etags[type] = etag
      }
    })
    await cache.setItems(merges.flatMap(x => x.cacheItems))
    await cache.deleteItems([...merges.flatMap(x => x.cacheDeleteIds), ...removedIds])

    await afterUpdate?.()
  }

  function mergeDocuments(type: TType, documentSet: DocumentSet<Entity> | undefined, loadAll: boolean) {
    const stateKey = typeToStateKey[type]
    const documents: EntityDict<DeletableEntity> = documentSet?.documents ?? {}
    if (loadAll) {
      state[stateKey] = documents
      void cache.deleteByType(type)
      return { cacheItems: Object.values(toRaw(state[stateKey])), cacheDeleteIds: [] as string[] }
    }
    const deletedIds = Object.values(documents)
      .filter(x => x.deleted)
      .map(x => x.id)
    const merged: EntityDict<Entity> = { ...state[stateKey], ...documents }
    deletedIds.forEach(x => delete merged[x])
    state[stateKey] = merged
    return { cacheItems: Object.values(documents).filter(x => !x.deleted), cacheDeleteIds: deletedIds }
  }

  function isType(type: string): type is TType {
    return type in typeToStateKey
  }

  async function handleEntitiesUpdated(changedTypes: string[]) {
    const camelCaseTypes = changedTypes.map(toCamelCase)
    const typesToFetch = camelCaseTypes.includes('courseOrganization')
      ? [...new Set([...camelCaseTypes, ...releaseDerivedTypes])]
      : camelCaseTypes
    await updateByTypes(typesToFetch.filter(isType))
  }

  return {
    initialUpdate,
    getCachedOrganizationId,
    switchCachedOrganization,
    setStateFromCache,
    whenAvailable,
    updateEntities,
    updateByTypes,
    handleEntitiesUpdated
  }
}
